/**
 * UserDrawer — centered modal with 6 tabs:
 * Profile · Roles & Access · Skills · Certifications · Technician & Work · Activity
 *
 * Replaces the old side-pane drawer. Centered, separate, with more fields.
 * Entra/3CX/ConnectWise linking moved to Integrations page.
 */
import React from "react";
import { X, User, Shield, Award, Wrench, Activity, Save, Trash2, Plus, GraduationCap, Upload, Palette, Link, Phone, Cloud } from "lucide-react";
import { useGet, apiPatch, apiPost, apiDelete } from "../hooks/useApi.js";
import { SkillsMatrix } from "./SkillsMatrix.js";
import { ROLE_COLORS, ROLE_LABEL } from "../lib/roles.js";

type Tab = "profile" | "roles" | "skills" | "certs" | "techwork" | "integrations" | "activity";

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: "profile", label: "Profile", icon: User },
  { id: "roles", label: "Roles & Access", icon: Shield },
  { id: "skills", label: "Skills", icon: Award },
  { id: "certs", label: "Certifications", icon: GraduationCap },
  { id: "techwork", label: "Technician & Work", icon: Wrench },
  { id: "integrations", label: "Integrations", icon: Link },
  { id: "activity", label: "Activity", icon: Activity },
];

const AVATAR_COLORS = [
  "#FF6B35", "#F7931E", "#FFD23F", "#06A77D", "#0EA5E9",
  "#6366F1", "#8B5CF6", "#EC4899", "#EF4444", "#14B8A6",
  "#84CC16", "#F59E0B", "#3B82F6", "#A855F7", "#64748B",
];

type UserDetail = {
  id: string; email: string; name: string | null; firstName: string | null; lastName: string | null;
  title: string | null; phone: string | null; photoUrl: string | null; avatarColor: string | null;
  mobile: string | null; addressStreet: string | null; addressCity: string | null;
  addressState: string | null; addressZip: string | null; addressCountry: string | null;
  notes: string | null; role: string; tenantId: string; deactivated: boolean; createdAt: string;
  memberships: Array<{ tenantId: string; role: string }>;
  threecxAvailableExtensions: string[];
  entra: { entraOid: string | null; entraUpn: string | null; email: string };
  cwMembers: Array<{ cwMemberId: number; identifier: string; name: string; linkedUserId: string | null }>;
  technician: {
    id: string; name: string; cwMemberId: number | null; threecxExtension: string | null;
    tier: string | null; active: boolean; userId: string | null;
    maxOpenTickets: number | null; onsiteEligible: boolean | null;
    costRate: number | null; billingRate: number | null;
  } | null;
  skills: Array<{
    techSkillId: string | null; skillId: string; skillName: string; category: string;
    level: number; source: string;
  }>;
};

type WorkProfile = {
  timezone: string; dailyCapacityMinutes: number; billableTargetMinutes: number | null;
  lunchStart: string; lunchEnd: string; adminTimeMinutes: number; adminStartTime: string;
  trainingTimeMinutes: number; trainingRecurrence: string; trainingDayOfWeek: number; trainingStartTime: string;
};

type Certification = { id: string; name: string; issuer: string | null; issuedAt: string | null; expiresAt: string | null };

const SKILL_LEVELS = ["None", "Aware", "Capable", "Proficient", "Expert"];

function formatE164(raw: string): string {
  // Auto-format E164: strip non-digits, add +1 if US 10-digit
  let digits = raw.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) digits = digits.slice(1);
  if (digits.length === 10 && !digits.startsWith("1")) digits = "1" + digits;
  if (digits.length > 0) return "+" + digits;
  return "";
}

function initials(name: string | null, email: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0] ?? "";
    const last = parts[parts.length - 1] ?? "";
    if (parts.length >= 2 && first && last) {
      return (first.charAt(0) + last.charAt(0)).toUpperCase();
    }
    if (first) return first.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

export function UserDrawer({ userId, onClose, canEdit, isSelf }: { userId: string | null; onClose: () => void; canEdit: boolean; isSelf: boolean }) {
  const [tab, setTab] = React.useState<Tab>("profile");
  const url = userId ? "/api/v1/users/" + userId : null;
  const { data, loading, error, refetch } = useGet<UserDetail>(url);
  const [saveErr, setSaveErr] = React.useState("");
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (!userId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [userId, onClose]);

  if (!userId) return null;

  async function doSave(patch: Record<string, unknown>) {
    if (!userId) return;
    try {
      setSaveErr(""); await apiPatch("/api/v1/users/" + userId + "/details", patch);
      setSaved(true); setTimeout(() => setSaved(false), 2000); refetch();
    } catch (e) { setSaveErr(e instanceof Error ? e.message : "Save failed"); }
  }

  const bgColor = data?.avatarColor || "#6366F1";

  const isOverlay = !isSelf;
  const page = (
    <div className="flex h-full min-h-0">
      {/* Sidebar tabs */}
      <aside className="w-56 shrink-0 border-r border-[hsl(var(--border))] flex flex-col">
        <div className="p-4 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-3">
            {data?.photoUrl ? (
              <img src={data.photoUrl} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
            ) : (
              <div
                className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0 select-none"
                style={{ backgroundColor: bgColor }}
              >
                {initials(data?.name ?? null, data?.email ?? "?")}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[hsl(var(--content))] truncate">
                {data?.firstName && data?.lastName ? data.firstName + " " + data.lastName : data?.name || data?.email?.split("@")[0] || "Loading..."}
              </p>
              <p className="text-xs text-[hsl(var(--content-subtle))] truncate">{data?.email}</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={
                  "flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left " +
                  (active
                    ? "bg-brand-50 dark:bg-brand-950/30 text-brand-600 dark:text-brand-400"
                    : "text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] hover:text-[hsl(var(--content))]")
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between px-6 py-3 border-b border-[hsl(var(--border))] shrink-0">
          <h2 className="text-base font-semibold text-[hsl(var(--content))]">
            {TABS.find((t) => t.id === tab)?.label}
          </h2>
          {isOverlay && (
            <button onClick={onClose} className="text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] p-2 rounded-lg hover:bg-[hsl(var(--surface-subtle))] transition-colors">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
          {loading && <div className="text-center py-8 text-[hsl(var(--content-subtle))]">Loading...</div>}
          {error && <div className="text-center py-8 text-red-500">{error}</div>}
          {data && !loading && !error && (
            <>
              {tab === "profile" && <ProfileTab data={data} canEdit={canEdit} onSave={doSave} saved={saved} saveErr={saveErr} />}
              {tab === "roles" && <RolesTab data={data} />}
              {tab === "skills" && <SkillsMatrix skills={data.skills.map((s) => ({ skillId: s.skillId, skillName: s.skillName, category: s.category, level: s.level, source: s.source, inferred: s.source === "inferred", confidence: undefined }))} canEdit={canEdit} onSetLevel={async (skillId, level) => { try { await apiPatch("/api/v1/users/" + userId + "/skills/" + skillId, { level }); refetch(); } catch (e) { alert(e instanceof Error ? e.message : "Failed"); } }} />}
              {tab === "certs" && <CertsTab data={data} canEdit={canEdit} refetch={refetch} />}
              {tab === "techwork" && <TechWorkTab data={data} canEdit={canEdit} onSave={doSave} userId={userId} />}
              {tab === "integrations" && <IntegrationsTab data={data} canEdit={canEdit} onSave={doSave} />}
              {tab === "activity" && <ActivityTab data={data} />}
            </>
          )}
        </div>
        {saved && (
          <div className="px-6 py-2 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 text-sm text-center shrink-0">
            ✓ Saved
          </div>
        )}
        {saveErr && (
          <div className="px-6 py-2 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 text-sm text-center shrink-0">
            {saveErr}
          </div>
        )}
      </div>
    </div>
  );

  if (isOverlay) {
    return (
      <div className="fixed inset-0 z-50 bg-[hsl(var(--surface))]" onClick={onClose}>
        <div className="h-full" onClick={(e) => e.stopPropagation()}>{page}</div>
      </div>
    );
  }
  return page;
}

// ─── Profile Tab ───
function ProfileTab({ data, canEdit, onSave, saved, saveErr }: {
  data: UserDetail; canEdit: boolean; onSave: (p: Record<string, unknown>) => void; saved: boolean; saveErr: string;
}) {
  const [firstName, setFirstName] = React.useState(data.firstName ?? "");
  const [lastName, setLastName] = React.useState(data.lastName ?? "");
  const [title, setTitle] = React.useState(data.title ?? "");
  const [phone, setPhone] = React.useState(data.phone ?? "");
  const [mobile, setMobile] = React.useState(data.mobile ?? "");
  const [street, setStreet] = React.useState(data.addressStreet ?? "");
  const [city, setCity] = React.useState(data.addressCity ?? "");
  const [stateVal, setStateVal] = React.useState(data.addressState ?? "");
  const [zip, setZip] = React.useState(data.addressZip ?? "");
  const [country, setCountry] = React.useState(data.addressCountry ?? "USA");
  const [notes, setNotes] = React.useState(data.notes ?? "");
  const [avatarColor, setAvatarColor] = React.useState(data.avatarColor ?? "");
  const [photoUrl, setPhotoUrl] = React.useState(data.photoUrl ?? "");
  const fileRef = React.useRef<HTMLInputElement>(null);

  function handlePhone(raw: string, setter: (v: string) => void) {
    setter(formatE164(raw));
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 256 * 1024) { alert("Image must be under 256KB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setPhotoUrl(reader.result as string); };
    reader.readAsDataURL(file);
  }

  function save() {
    onSave({
      firstName, lastName, title,
      phone: phone || null,
      mobile: mobile || null,
      addressStreet: street || null,
      addressCity: city || null,
      addressState: stateVal || null,
      addressZip: zip || null,
      addressCountry: country || null,
      notes: notes || null,
      avatarColor: avatarColor || null,
      photoUrl: photoUrl || null,
    });
  }

  return (
    <div className="space-y-5">
      {/* Avatar section */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-4">Avatar</h3>
        <div className="flex items-start gap-6">
          <div className="flex flex-col items-center gap-2">
            {photoUrl ? (
              <img src={photoUrl} alt="" className="h-20 w-20 rounded-full object-cover border-2 border-[hsl(var(--border))]" />
            ) : (
              <div
                className="h-20 w-20 rounded-full flex items-center justify-center font-bold text-white text-2xl"
                style={{ backgroundColor: avatarColor || "#6366F1" }}
              >
                {initials(firstName ? firstName + " " + lastName : data.name, data.email)}
              </div>
            )}
          </div>
          <div className="flex-1 space-y-3">
            {canEdit && (
              <>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                  >
                    <Upload className="h-4 w-4" /> Upload Photo
                  </button>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFileUpload} className="hidden" />
                  {photoUrl && (
                    <button
                      type="button"
                      onClick={() => setPhotoUrl("")}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-900 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" /> Remove
                    </button>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2 text-xs text-[hsl(var(--content-subtle))] mb-2">
                    <Palette className="h-3.5 w-3.5" /> Or pick a color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AVATAR_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => { setAvatarColor(c); setPhotoUrl(""); }}
                        className={"h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 " + (avatarColor === c ? "border-content ring-2 ring-offset-1" : "border-transparent")}
                        style={{ backgroundColor: c }}
                        aria-label={"Pick color " + c}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Personal info */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-4">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Personal Information</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First Name" value={firstName} onChange={setFirstName} canEdit={canEdit} />
          <Field label="Last Name" value={lastName} onChange={setLastName} canEdit={canEdit} />
          <Field label="Title" value={title} onChange={setTitle} canEdit={canEdit} placeholder="Senior Technician" />
          <Field label="Email" value={data.email} onChange={() => {}} canEdit={false} hint="Managed by Microsoft Entra ID" />
          <Field label="Phone" value={phone} onChange={(v) => handlePhone(v, setPhone)} canEdit={canEdit} placeholder="+15551234567" hint="E.164 format" />
          <Field label="Mobile" value={mobile} onChange={(v) => handlePhone(v, setMobile)} canEdit={canEdit} placeholder="+15551234567" hint="E.164 format" />
        </div>
      </div>

      {/* Address */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-4">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Address</h3>
        <Field label="Street" value={street} onChange={setStreet} canEdit={canEdit} placeholder="123 Main St" full />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="City" value={city} onChange={setCity} canEdit={canEdit} />
          <Field label="State" value={stateVal} onChange={setStateVal} canEdit={canEdit} placeholder="TX" />
          <Field label="ZIP" value={zip} onChange={setZip} canEdit={canEdit} />
        </div>
        <Field label="Country" value={country} onChange={setCountry} canEdit={canEdit} placeholder="USA" full />
      </div>

      {/* Notes */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-2">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Notes</h3>
        {canEdit ? (
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder="Admin notes..."
          />
        ) : (
          <p className="text-sm text-[hsl(var(--content-subtle))]">{notes || "No notes."}</p>
        )}
      </div>

      {canEdit && (
        <button
          onClick={save}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 transition-colors"
        >
          <Save className="h-4 w-4" /> Save Changes
        </button>
      )}
    </div>
  );
}

// ─── Roles Tab ───
function RolesTab({ data }: { data: UserDetail }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[hsl(var(--border))] p-5">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3">Current Role</h3>
        <span className={"inline-flex px-3 py-1 rounded-full text-xs font-semibold " + (ROLE_COLORS[data.role as keyof typeof ROLE_COLORS] ?? "bg-gray-100 text-gray-700")}>
          {ROLE_LABEL[data.role as keyof typeof ROLE_LABEL] ?? data.role}
        </span>
      </div>
      <div className="rounded-xl border border-[hsl(var(--border))] p-5">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3">All Memberships</h3>
        <div className="space-y-2">
          {data.memberships.map((m, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-[hsl(var(--surface-subtle))] px-3 py-2">
              <span className="text-sm font-mono text-[hsl(var(--content-subtle))]">{m.tenantId}</span>
              <span className="text-sm font-medium text-[hsl(var(--content))]">{ROLE_LABEL[m.role as keyof typeof ROLE_LABEL] ?? m.role}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Skills Tab (grouped by board/category with set-all) ───
function SkillsTab({ data, canEdit, userId, refetch }: {
  data: UserDetail; canEdit: boolean; userId: string; onSave: (p: Record<string, unknown>) => void; refetch: () => void;
}) {
  const [bulkLevel, setBulkLevel] = React.useState<Record<string, string>>({});

  async function setLevel(skillId: string, level: number) {
    try {
      await apiPatch("/api/v1/users/" + userId + "/skills/" + skillId, { level });
      refetch();
    } catch (e) { alert(e instanceof Error ? e.message : "Failed"); }
  }

  async function setAllForGroup(skills: typeof data.skills, level: number) {
    try {
      await Promise.all(skills.map((s) => apiPatch("/api/v1/users/" + userId + "/skills/" + s.skillId, { level })));
      refetch();
    } catch (e) { alert(e instanceof Error ? e.message : "Failed"); }
  }

  const groups = React.useMemo(() => {
    const map = new Map<string, typeof data.skills>();
    for (const s of data.skills) {
      const cat = s.category || "Uncategorized";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data.skills]);

  if (data.skills.length === 0) {
    return <p className="text-sm text-[hsl(var(--content-subtle))] text-center py-4">No skills assigned.</p>;
  }

  return (
    <div className="space-y-4">
      {groups.map(([category, skills]) => (
        <div key={category} className="rounded-xl border border-[hsl(var(--border))] overflow-hidden">
          <div className="px-4 py-2 bg-[hsl(var(--surface-subtle))] border-b border-[hsl(var(--border))] flex items-center justify-between">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">{category}</h3>
              <p className="text-xs text-[hsl(var(--content-subtle))]">{skills.length} skill{skills.length !== 1 ? "s" : ""}</p>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[hsl(var(--content-subtle))]">Set all:</span>
                <select
                  value={bulkLevel[category] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBulkLevel((prev) => ({ ...prev, [category]: val }));
                    if (val) setAllForGroup(skills, Number(val));
                  }}
                  className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-xs text-[hsl(var(--content))] focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">—</option>
                  {SKILL_LEVELS.map((lbl, idx) => <option key={idx} value={idx}>{lbl}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {skills.map((s) => (
              <div key={s.skillId} className="flex items-center justify-between px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{s.skillName}</p>
                  <p className="text-xs text-[hsl(var(--content-subtle))]">{s.source}</p>
                </div>
                {canEdit ? (
                  <select
                    value={s.level}
                    onChange={(e) => setLevel(s.skillId, Number(e.target.value))}
                    className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-sm text-[hsl(var(--content))] focus:outline-none focus:ring-2 focus:ring-brand-500 shrink-0 ml-3"
                  >
                    {SKILL_LEVELS.map((lbl, idx) => <option key={idx} value={idx}>{lbl}</option>)}
                  </select>
                ) : (
                  <span className="text-sm text-[hsl(var(--content-subtle))] shrink-0 ml-3">{SKILL_LEVELS[s.level]}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Certifications Tab ───
function CertsTab({ data, canEdit, refetch }: {
  data: UserDetail; canEdit: boolean; refetch: () => void;
}) {
  const [certs, setCerts] = React.useState<Certification[]>([]);
  const [name, setName] = React.useState("");
  const [issuer, setIssuer] = React.useState("");
  const [issuedAt, setIssuedAt] = React.useState("");
  const [expiresAt, setExpiresAt] = React.useState("");

  React.useEffect(() => {
    if (data.technician) {
      fetch("/api/v1/technicians/" + data.technician.id + "/certifications")
        .then((r) => r.json())
        .then((d) => setCerts(d.items ?? []))
        .catch(() => {});
    }
  }, [data.technician]);

  async function addCert() {
    if (!data.technician || !name) return;
    try {
      await apiPost("/api/v1/technicians/" + data.technician.id + "/certifications", {
        name, issuer: issuer || null, issuedAt: issuedAt || null, expiresAt: expiresAt || null,
      });
      setName(""); setIssuer(""); setIssuedAt(""); setExpiresAt("");
      const r = await fetch("/api/v1/technicians/" + data.technician.id + "/certifications");
      const d = await r.json();
      setCerts(d.items ?? []);
    } catch (e) { alert(e instanceof Error ? e.message : "Failed"); }
  }

  async function delCert(id: string) {
    if (!data.technician) return;
    try {
      await apiDelete("/api/v1/technicians/" + data.technician.id + "/certifications/" + id);
      setCerts((prev) => prev.filter((c) => c.id !== id));
    } catch (e) { alert(e instanceof Error ? e.message : "Failed"); }
  }

  return (
    <div className="space-y-4">
      {canEdit && data.technician && (
        <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-3">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Add Certification</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Certification name" className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <input value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="Issuer" className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <button onClick={addCert} className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 transition-colors">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      )}
      <div className="space-y-2">
        {certs.length === 0 && <p className="text-sm text-[hsl(var(--content-subtle))] text-center py-4">No certifications.</p>}
        {certs.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-4 py-3">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--content))]">{c.name}</p>
              <p className="text-xs text-[hsl(var(--content-subtle))]">
                {c.issuer ?? "—"}
                {c.issuedAt && " · Issued " + new Date(c.issuedAt).toLocaleDateString()}
                {c.expiresAt && " · Expires " + new Date(c.expiresAt).toLocaleDateString()}
              </p>
            </div>
            {canEdit && (
              <button onClick={() => delCert(c.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Combined Technician & Work Tab ───
function TechWorkTab({ data, canEdit, onSave, userId }: {
  data: UserDetail; canEdit: boolean; onSave: (p: Record<string, unknown>) => void; userId: string;
}) {
  const tech = data.technician;
  const [tier, setTier] = React.useState(tech?.tier ?? "");
  const [maxOpen, setMaxOpen] = React.useState(tech?.maxOpenTickets?.toString() ?? "");
  const [onsite, setOnsite] = React.useState(tech?.onsiteEligible ?? true);
  const [costRate, setCostRate] = React.useState(tech?.costRate?.toString() ?? "");
  const [billingRate, setBillingRate] = React.useState(tech?.billingRate?.toString() ?? "");

  const [wp, setWp] = React.useState<WorkProfile | null>(null);
  React.useEffect(() => {
    if (tech) {
      fetch("/api/v1/technicians/" + tech.id + "/work-profile")
        .then((r) => r.json())
        .then(setWp)
        .catch(() => {});
    }
  }, [tech]);

  async function saveTech() {
    if (!tech) return;
    try {
      await apiPatch("/api/v1/technicians/" + tech.id, {
        tier: tier || null,
        maxOpenTickets: maxOpen ? Number(maxOpen) : null,
        onsiteEligible: onsite,
        costRate: costRate ? Number(costRate) : null,
        billingRate: billingRate ? Number(billingRate) : null,
      });
      alert("Saved");
    } catch (e) { alert(e instanceof Error ? e.message : "Failed"); }
  }

  async function saveWp() {
    if (!tech || !wp) return;
    try {
      await apiPatch("/api/v1/technicians/" + tech.id + "/work-profile", wp);
      alert("Saved");
    } catch (e) { alert(e instanceof Error ? e.message : "Failed"); }
  }

  if (!tech) {
    return <p className="text-sm text-[hsl(var(--content-subtle))] text-center py-8">No technician record linked to this user.</p>;
  }

  return (
    <div className="space-y-5">
      {/* Technician fields */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-4">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Technician Settings</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs text-[hsl(var(--content-subtle))]">Tier</label>
            {canEdit ? (
              <select value={tier} onChange={(e) => setTier(e.target.value)} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                <option value="">—</option>
                <option value="L1">L1</option>
                <option value="L2">L2</option>
                <option value="L3">L3</option>
              </select>
            ) : <p className="text-sm text-[hsl(var(--content))] mt-1">{tier || "—"}</p>}
          </div>
          <Field label="Max Open Tickets" value={maxOpen} onChange={setMaxOpen} canEdit={canEdit} placeholder="10" hint="Per technician" />
          <Field label="Cost Rate ($/hr)" value={costRate} onChange={setCostRate} canEdit={canEdit} placeholder="50" hint="Internal hourly cost" />
          <Field label="Billing Rate ($/hr)" value={billingRate} onChange={setBillingRate} canEdit={canEdit} placeholder="125" hint="Default hourly bill rate" />
          <div className="flex items-center gap-2">
            <input type="checkbox" id="onsite" checked={onsite} onChange={(e) => setOnsite(e.target.checked)} disabled={!canEdit} className="rounded border-[hsl(var(--border))]" />
            <label htmlFor="onsite" className="text-sm text-[hsl(var(--content))]">Onsite eligible</label>
          </div>
        </div>
        {canEdit && (
          <button onClick={saveTech} className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 transition-colors">
            <Save className="h-4 w-4" /> Save Technician
          </button>
        )}
      </div>

      {/* Work profile fields */}
      {wp && (
        <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Work Profile</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Timezone</label>
              <select value={wp.timezone} onChange={(e) => setWp({ ...wp, timezone: e.target.value })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                {["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "America/Phoenix", "America/Anchorage", "America/Indiana/Indianapolis", "UTC"].map((tz) => <option key={tz} value={tz}>{tz.replace("America/", "").replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Daily Capacity (hrs/day)</label>
              <input type="number" step="0.5" value={(wp.dailyCapacityMinutes / 60).toString()} onChange={(e) => setWp({ ...wp, dailyCapacityMinutes: Math.round(Number(e.target.value) * 60) })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Billable Target (hrs/day)</label>
              <input type="number" step="0.5" value={wp.billableTargetMinutes ? (wp.billableTargetMinutes / 60).toString() : ""} onChange={(e) => setWp({ ...wp, billableTargetMinutes: e.target.value ? Math.round(Number(e.target.value) * 60) : null })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Lunch Start</label>
              <input type="time" value={wp.lunchStart} onChange={(e) => setWp({ ...wp, lunchStart: e.target.value })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Lunch End</label>
              <input type="time" value={wp.lunchEnd} onChange={(e) => setWp({ ...wp, lunchEnd: e.target.value })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Admin Time (hrs/day)</label>
              <input type="number" step="0.5" value={(wp.adminTimeMinutes / 60).toString()} onChange={(e) => setWp({ ...wp, adminTimeMinutes: Math.round(Number(e.target.value) * 60) })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Admin Start Time</label>
              <input type="time" value={wp.adminStartTime} onChange={(e) => setWp({ ...wp, adminStartTime: e.target.value })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--content-subtle))]">Training Time (hrs/week)</label>
              <input type="number" step="0.5" value={(wp.trainingTimeMinutes / 60).toString()} onChange={(e) => setWp({ ...wp, trainingTimeMinutes: Math.round(Number(e.target.value) * 60) })} disabled={!canEdit} className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>
          {canEdit && (
            <button onClick={saveWp} className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 transition-colors">
              <Save className="h-4 w-4" /> Save Work Profile
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Integrations Tab ───
function IntegrationsTab({ data, canEdit, onSave }: {
  data: UserDetail; canEdit: boolean; onSave: (p: Record<string, unknown>) => void;
}) {
  const [entraUpn, setEntraUpn] = React.useState(data.entra?.entraUpn ?? "");
  const [threecxExt, setThreecxExt] = React.useState(data.technician?.threecxExtension ?? "");
  const [selectedCwMember, setSelectedCwMember] = React.useState<string>("");
  const [threecxDDir, setThreecxDDir] = React.useState<Array<{ extension: string; displayName: string; enabled: boolean; assignedUserId: string | null }>>([]);
  React.useEffect(() => {
    fetch("/api/v1/users/3cx-directory")
      .then((r) => r.json())
      .then((d) => { if (d.items) setThreecxDDir(d.items); })
      .catch(() => {});
  }, []);
  const [linkResult, setLinkResult] = React.useState("");

  async function saveEntra() {
    onSave({ entraUpn: entraUpn || null });
  }
  async function save3cx() {
    onSave({ threecxExtension: threecxExt || null });
  }
  async function linkCw() {
    if (!selectedCwMember || !data.technician) return;
    try {
      await apiPatch("/api/v1/technicians/" + data.technician.id, { cwMemberId: Number(selectedCwMember) });
      setLinkResult("Linked successfully");
      setTimeout(() => setLinkResult(""), 3000);
    } catch (e) {
      setLinkResult(e instanceof Error ? e.message : "Failed");
    }
  }

  return (
    <div className="space-y-5">
      {/* Entra ID */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Cloud className="h-5 w-5 text-blue-500" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Microsoft Entra ID</h3>
        </div>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-[hsl(var(--content-subtle))]">Object ID</dt>
            <dd className="font-mono text-xs text-[hsl(var(--content-subtle))]">{data.entra?.entraOid ?? "—"}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[hsl(var(--content-subtle))]">Email</dt>
            <dd className="text-[hsl(var(--content))]">{data.email}</dd>
          </div>
        </dl>
        <div>
          <label className="text-xs text-[hsl(var(--content-subtle))]">UPN Override (optional)</label>
          <div className="flex gap-2 mt-1">
            <input
              value={entraUpn}
              onChange={(e) => setEntraUpn(e.target.value)}
              disabled={!canEdit}
              placeholder="user@houstontechsys.net"
              className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            {canEdit && (
              <button onClick={saveEntra} className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 transition-colors shrink-0">
                <Save className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 3CX */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Phone className="h-5 w-5 text-green-500" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">3CX Phone Extension</h3>
        </div>
        <div>
          <label className="text-xs text-[hsl(var(--content-subtle))]">Extension</label>
          <div className="flex gap-2 mt-1">
            {canEdit ? (
              <select
                value={threecxExt}
                onChange={(e) => setThreecxExt(e.target.value)}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">—</option>
                {threecxDDir.length > 0 ? threecxDDir.filter((d) => d.enabled).map((d) => (
                  <option key={d.extension} value={d.extension}>{d.extension} — {d.displayName}{d.assignedUserId ? " (assigned)" : ""}</option>
                )) : data.threecxAvailableExtensions.map((ext) => (
                  <option key={ext} value={ext}>{ext}</option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-[hsl(var(--content))] mt-1">{threecxExt || "Not assigned"}</p>
            )}
            {canEdit && (
              <button onClick={save3cx} className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 transition-colors shrink-0">
                <Save className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">{threecxDDir.length > 0 ? threecxDDir.filter((d) => d.enabled).length + " extensions from 3CX directory" : "Fallback pool: " + data.threecxAvailableExtensions.length + " extensions"}</p>
        </div>
      </div>

      {/* ConnectWise */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Link className="h-5 w-5 text-purple-500" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">ConnectWise Member</h3>
        </div>
        {data.technician?.cwMemberId ? (
          <p className="text-sm text-green-600">✓ Linked (CW Member ID: {data.technician.cwMemberId})</p>
        ) : (
          <p className="text-sm text-[hsl(var(--content-subtle))]">Not linked to a ConnectWise member.</p>
        )}
        {canEdit && data.cwMembers && data.cwMembers.length > 0 && (
          <div>
            <label className="text-xs text-[hsl(var(--content-subtle))]">Link to CW Member</label>
            <div className="flex gap-2 mt-1">
              <select
                value={selectedCwMember}
                onChange={(e) => setSelectedCwMember(e.target.value)}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">Select member…</option>
                {data.cwMembers.map((m) => (
                  <option key={m.cwMemberId} value={m.cwMemberId}>
                    {m.identifier} — {m.name}{m.linkedUserId ? " (linked)" : ""}
                  </option>
                ))}
              </select>
              <button
                onClick={linkCw}
                disabled={!selectedCwMember}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 transition-colors shrink-0 disabled:opacity-50"
              >
                <Link className="h-4 w-4" />
              </button>
            </div>
            {linkResult && <p className="text-xs text-green-600 mt-1">{linkResult}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Activity Tab ───
function ActivityTab({ data }: { data: UserDetail }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[hsl(var(--border))] p-5">
        <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3">Account Summary</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-[hsl(var(--content-subtle))]">User ID</dt><dd className="font-mono text-xs text-[hsl(var(--content))]">{data.id}</dd></div>
          <div className="flex justify-between"><dt className="text-[hsl(var(--content-subtle))]">Created</dt><dd className="text-[hsl(var(--content))]">{new Date(data.createdAt).toLocaleString()}</dd></div>
          <div className="flex justify-between"><dt className="text-[hsl(var(--content-subtle))]">Status</dt><dd className={data.deactivated ? "text-red-500" : "text-green-500"}>{data.deactivated ? "Deactivated" : "Active"}</dd></div>
          <div className="flex justify-between"><dt className="text-[hsl(var(--content-subtle))]">Memberships</dt><dd className="text-[hsl(var(--content))]">{data.memberships.length}</dd></div>
          <div className="flex justify-between"><dt className="text-[hsl(var(--content-subtle))]">Skills</dt><dd className="text-[hsl(var(--content))]">{data.skills.length}</dd></div>
        </dl>
      </div>
    </div>
  );
}

// ─── Reusable Field ───
function Field({ label, value, onChange, canEdit, placeholder, hint, full }: {
  label: string; value: string; onChange: (v: string) => void; canEdit: boolean;
  placeholder?: string; hint?: string; full?: boolean;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs text-[hsl(var(--content-subtle))]">{label}</label>
      {canEdit ? (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      ) : (
        <p className="text-sm text-[hsl(var(--content))] mt-1">{value || "—"}</p>
      )}
      {hint && <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">{hint}</p>}
    </div>
  );
}
